import {checkoutRecoveryCaptchaToken} from './contact_captcha_utils';
import {applyCoupon} from './discount_code_api_utils';
import {detectTimezone} from './timezone_utils';

export const recoveryEnabled = () => document.querySelector('meta[name="checkout-recovery-enabled"]')?.content === 'true';
const validEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254;

// Only this allowlist leaves the browser. Never serialize checkout/payment state.
export function recoverySnapshot(state) {
  const address = {};
  ['street','city','state','zipcode','county','apartment','formatted_address','latitude','longitude'].forEach(k => {address[k] = state[k] || '';});
  return {address, sub_service_ids: Object.keys(state.selectedSubservices || {}).map(Number), coupon_code: state.coupon?.code || ''};
}

export class CheckoutRecoveryTracker {
  constructor(snapshot) {
    this.snapshot = snapshot;
    this.email = '';
    this.disposed = false;
    this.lastActivity = 0;
    this.busy = false;
    this.activity = () => {
      if (!this.email || this.disposed || document.visibilityState !== 'visible' || Date.now() - this.lastActivity < 30000) return;
      this.lastActivity = Date.now();
      clearTimeout(this.activityTimer);
      this.activityTimer = setTimeout(() => {
        if (!this.disposed) $.ajax({type:'POST',url:'/checkout_recovery/activity',data:{snapshot:this.snapshot()}}).catch(() => {});
      },500);
    };
    ['pointerdown','keydown','input'].forEach(event => document.addEventListener(event,this.activity));
  }
  emailChanged(value, immediate = false) {
    if (!recoveryEnabled() || this.disposed) return;
    clearTimeout(this.timer);
    clearTimeout(this.activityTimer);
    const email = value.trim().toLowerCase();
    this.desiredEmail = email;
    // Stop the earlier recipient immediately, even if verifying the corrected
    // address later fails. Never leave a typo queued for a reminder.
    if (this.email && email !== this.email) {
      $.ajax({type:'POST',url:'/checkout_recovery/activity',data:{withdraw:true}}).catch(() => {});
      this.email = '';
    }
    if (!validEmail(email)) {
      this.email = '';
      return;
    }
    this.timer = setTimeout(() => this.capture(email),immediate ? 0 : 1000);
  }
  async capture(email) {
    if (this.disposed || this.busy) return;
    this.busy = true;
    try {
      const captcha = await checkoutRecoveryCaptchaToken();
      if (this.disposed || this.desiredEmail !== email) return;
      await $.ajax({type:'POST',url:'/checkout_recovery',data:{email,captcha,snapshot:this.snapshot()}});
      if (this.desiredEmail !== email) {
        await $.ajax({type:'POST',url:'/checkout_recovery/activity',data:{withdraw:true}});
        return;
      }
      this.email = email;
      this.lastActivity = Date.now();
    } catch (_) { /* Recovery must never block checkout or show checkout errors. */ }
    finally {
      this.busy = false;
      if (!this.disposed && this.desiredEmail !== email && validEmail(this.desiredEmail || '')) this.capture(this.desiredEmail);
    }
  }
  dispose() {
    this.disposed = true;
    clearTimeout(this.timer);
    clearTimeout(this.activityTimer);
    ['pointerdown','keydown','input'].forEach(event => document.removeEventListener(event,this.activity));
  }
}

export function resumeToken() {
  return new URLSearchParams(window.location.hash.slice(1)).get('resume_checkout');
}

export async function restoreRecovery(component) {
  const token = resumeToken();
  if (!token || !recoveryEnabled()) return;
  // The token is a fragment (never part of request logs or the referrer).
  window.history.replaceState(window.history.state,'',window.location.pathname + window.location.search);
  try {
    const saved = await $.ajax({url:'/checkout_recovery/resume',data:{token}});
    const selectedSubservices = {}, package_attributes = {};
    Object.values(component.props.services || {}).filter(s => !s.hidden && !s.reshoot).forEach(service => {
      (service.subServices || []).filter(s => saved.sub_service_ids.includes(s.id) && !s.hidden && !s.reshoot).forEach(sub => {
        selectedSubservices[sub.id] = sub;
        if (!package_attributes[service.id]) package_attributes[service.id] = {service_id:service.id,package_sub_services_attributes:{}};
        package_attributes[service.id].package_sub_services_attributes[sub.id] = {sub_service_id:sub.id};
      });
    });
    component.setState({...saved.address,selectedSubservices,package_attributes,appointments:{},page:1,initialPage:1});
    if (saved.address.latitude && saved.address.longitude) {
      const zone = await detectTimezone(saved.address.latitude,saved.address.longitude);
      component.setState({detected_timezone:zone});
    }
    if (saved.coupon_code) {
      const coupon = await applyCoupon(saved.coupon_code);
      if (coupon?.id) component.setState({coupon});
    }
  } catch (_) {
    component.props.showToast?.('This saved booking is no longer available. You can start a new booking.');
  }
}
